import type { ReactNode } from "react";

import { cn } from "@/shared/lib/cn";

type Tone = "error" | "success" | "info" | "warning";

interface AlertProps {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}

const TONES: Record<Tone, string> = {
  error:
    "border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200",
  success:
    "border-green-300 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200",
  info: "border-slate-300 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200",
  // Ni un échec, ni une simple information : quelque chose est en cours et
  // demande une décision — une suppression de compte programmée, par exemple.
  warning:
    "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200",
};

/**
 * Message d'état.
 *
 * `role="alert"` sur l'erreur uniquement : une région d'alerte interrompt le
 * lecteur d'écran, ce qui est justifié pour un échec et intrusif pour une
 * confirmation — celle-ci passe par `role="status"`, poli.
 */
export function Alert({ tone = "info", children, className }: AlertProps) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn("rounded-md border px-4 py-3 text-sm", TONES[tone], className)}
    >
      {children}
    </div>
  );
}
